import create from 'zustand'
import { useApplicationStore } from 'src/aplication-store'
import { HostCommunication } from 'src/host-communication'
import { ChatScope } from './chat-scope'
import { loadChat } from 'src/load-aplication-data'
import { lodash } from '@syonet/lang'

type FullPageControlType = {
    reference?: Window | null
    isOpen: () => boolean
}
const FullPageControl: FullPageControlType = {
    isOpen: function () {
        if (!this.reference) return false
        return !this.reference.closed
    }
}

export const useChatStore = create<ChatScope>((set, get) => {
    const executeOnShrink = () => {
        set(({ whatsapp, header }) => ({
            whatsapp: {
                ...whatsapp,
                loading: true,
                customerId: undefined,
                customerName: undefined,
                customerPhone: undefined
            },
            header: {
                ...header,
                phone: undefined,
                name: undefined
            },
            selectedContact: undefined,
            contactsOpened: true,
            filter: 'ALL'
        }))
        useApplicationStore.getState().shrinking()
    }

    return {
        contactsOpened: true,
        isLoading: false,
        isMobile: false,
        entries: [],
        itemCount: 0,
        itemsPerPage: 50,
        pageIndex: 0,
        itemsPerPageOptions: [10, 25, 50, 75, 100],
        filter: 'ALL',

        header: {
            initials: '',
            name: '',
            phone: '',
            tickets: [],

            onOpenContactList: (isMobile = false) => {
                if (isMobile) executeOnShrink()
                set((state) => ({ contactsOpened: isMobile ? true : !state.contactsOpened }))
            },

            onTicketSelect: (ticket) => {
                const hostCommunication = HostCommunication.singleton()
                hostCommunication.notifyTicketSelected(ticket)
            }
        },

        whatsapp: {
            customerId: undefined,
            customerName: '',
            companyId: undefined,
            companyPhone: '',
            sendHsm: false,
            userName: '',
            room: '',
            readOnly: false,
            eureka: undefined,
            customerPhone: '',
            loading: true,
            language: '',
            ready: false,
            error: false
        },

        decoration: {
            caption: 'WhatsApp',
            onClose: function () {
                executeOnShrink()
            },
            onExpand: function () {
                const appState = useApplicationStore.getState()
                const fullPageLink = appState.fullPageLink
                if (fullPageLink) {
                    let url: URL | null = null

                    const startWithProtocolRegex = /^.{0,5}:\/\//
                    const hasProtocol = fullPageLink.match(startWithProtocolRegex)

                    if (hasProtocol) {
                        url = new URL(fullPageLink)
                    } else {
                        const { protocol, host } = location
                        const baseURL = `${protocol}//${host}`
                        url = new URL(baseURL)
                        url.pathname = fullPageLink
                    }

                    if (FullPageControl.isOpen()) {
                        FullPageControl.reference?.focus()
                    } else {
                        FullPageControl.reference = window.open(url.toString())
                    }

                    executeOnShrink()
                }
            }
        },

        updateWhatsapp: function (whatsapp) {
            set((state) => ({
                whatsapp: {
                    ...state.whatsapp,
                    ...whatsapp
                }
            }))
        },

        onFilterChange: function (filter: 'UNREAD' | 'ALL') {
            set(() => ({ hasClickedFilter: true }))
            set(() => ({ isLoading: true }))
            set(() => ({ filter }))
            loadChat()
        },

        onCloseContactList: function () {
            set(() => ({ contactsOpened: false }))
        },

        cleanNotification: function () {
            const state = get()
            const entries = state.entries.map((e) => ({ ...e, quantity: 0 }))
            set(() => ({ entries }))
        },

        onChangeItemsPerPage: lodash.debounce(function (ev) {
            const itemsPerPage = +ev.target.value
            if (Number.isFinite(itemsPerPage)) {
                set(() => ({ itemsPerPage }))
                set(() => ({ pageIndex: 0 }))
                loadChat()
            }
        }, 250),

        onChangePageIndex: lodash.debounce(function (ev, i) {
            set(() => ({ pageIndex: i }))
            loadChat()
        }, 250),

        updateContacts: function (contacts) {
            set(() => ({ entries: [] }))
            set(() => ({ ...contacts }))
            set(() => ({ isLoading: false }))
        }
    }
})
